/**
 * Group standings, one group at a time.
 *
 * Rotating rather than tiled: eight groups on one screen is eight tables
 * nobody can read from the far side of a room, and a player wants their own
 * group big for a few seconds far more than they want all of them small.
 *
 * THE ROTATION STOPS AT THE KNOCKOUT. Once a bracket exists the groups are
 * history — who came second in group 3 decides nothing any more, and the side
 * panel belongs to the bracket. The page stops rendering this rather than
 * hiding it, so no timer is left running behind the bracket.
 *
 * Rows arrive from the room already sorted, by the same comparator the
 * standings endpoint uses. Nothing is re-sorted here: a TV and the admin's
 * standings tab disagreeing about who is top of a group is exactly the kind of
 * argument this screen exists to prevent.
 *
 * The venue's standings_show_* toggles are NOT applied here. Those govern the
 * extra-stat columns on the public season table, applied server-side by
 * public-standings.js, and none of the columns below are among them.
 */

import { formatAvg } from '../../lib/format.js';
import { useRotation } from './TvAds.jsx';

/** Long enough to find your own name and read the row it is on. */
export const GROUP_MS = 12_000;

export default function TvStandings({ groups, enabled = true }) {
  const group = useRotation(groups, GROUP_MS, enabled);

  if (!group) return null;

  return (
    <section className="hd-tv-standings">
      <header className="hd-tv-panel-head">
        <h2 className="hd-tv-panel-title">
          Group {group.groupNumber}
          {group.boardNumber != null && (
            <span className="hd-tv-panel-sub"> · Board {group.boardNumber}</span>
          )}
        </h2>

        {/* Which of how many, so a player knows whether their group has been
            and gone or is still to come. */}
        {groups.length > 1 && (
          <div className="hd-tv-dots" aria-hidden="true">
            {groups.map((g) => (
              <span
                key={g.groupNumber}
                className={`hd-tv-dot ${g.groupNumber === group.groupNumber ? 'hd-tv-dot-on' : ''}`}
              />
            ))}
          </div>
        )}
      </header>

      <table className="hd-tv-table">
        <thead>
          <tr>
            <th className="hd-tv-col-pos" scope="col">#</th>
            <th scope="col">Player</th>
            <th className="hd-tv-col-num" scope="col">P</th>
            <th className="hd-tv-col-num" scope="col">W</th>
            <th className="hd-tv-col-num" scope="col">L</th>
            <th className="hd-tv-col-num" scope="col">+/-</th>
            <th className="hd-tv-col-num" scope="col">Avg</th>
          </tr>
        </thead>

        <tbody>
          {group.standings.map((row, i) => (
            <tr key={row.entry_id}>
              <td className="hd-tv-col-pos">{i + 1}</td>
              <td className="hd-tv-standings-name">{row.display_name ?? 'TBC'}</td>
              <td className="hd-tv-col-num">{row.matches_played}</td>
              <td className="hd-tv-col-num">{row.matches_won}</td>
              <td className="hd-tv-col-num">{row.matches_lost}</td>
              <td className="hd-tv-col-num">
                {row.leg_difference > 0 ? `+${row.leg_difference}` : row.leg_difference}
              </td>
              <td className="hd-tv-col-num">{formatAvg(row.average)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
